import { watch } from 'vue';
import { useRoute } from 'vue-router';

import { storeToRefs } from 'pinia';

import api from '@/api/index';
import { useHangUpCommon } from '@/composables/domains/chat/useHangupCommon';
import { useNotification } from '@/composables/shared/useNotification';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useMicrophoneStore } from '@/stores/microphone';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { IMicrophoneWithVoice } from '@/types/components';
import { ConnectionInfo } from '@/types/socket';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import { stringToBase64UrlSafe } from '@/utils/encryptor.js';

export function useSocketCall(setBottomBarStatus?: (status: number) => void) {
  const audioStore = useAudioStore();
  const socketStore = useSocketStore();
  const callStore = useCallStore();
  const userStore = useUserStore();
  const microphoneStore = useMicrophoneStore();
  const route = useRoute();

  const { selectedUserId, selectedIsVoiceRegistered, selectedChatId, selectedNickname, evictMySelf } =
    storeToRefs(socketStore);

  const { handleHangUp } = useHangUpCommon({
    setBottomBarStatus: setBottomBarStatus ?? (() => {}),
  });

  const { userInfo } = storeToRefs(userStore);
  const { languageSelected } = storeToRefs(audioStore);
  const { qrCodeInfo } = storeToRefs(callStore);
  const { pageLanguage } = storeToRefs(userStore);

  const connectToWebsocket = async (isForceful = false, info?: Partial<ConnectionInfo>) => {
    if (setBottomBarStatus) setBottomBarStatus(1);

    await microphoneStore.initMicrophoneStore({
      isUsingMicWithVoice: info?.isTssOn === '1',
      isUsingDeviceAutoUpdate: info?.isTssOn === '1',
      isUsingSpecificGain: true,
      isUsingHintBox: true,
      isUsingToast: true,
    });
    const { voiceId = 0 } = microphoneStore.selectedMicrophone as IMicrophoneWithVoice;

    const isGuest = userInfo.value.userType === 'GUEST';
    const isPartnerPerson = isGuest ? '0' : '1';
    const agent = JSON.stringify(getBrowserAndDeviceInfo()); // 사용자 브라우저 및 장치 정보
    const authorization = localStorage.getItem('authToken')
      ? `Bearer ${localStorage.getItem('authToken')}`
      : `SC-USER-ID ${localStorage.getItem('SC-USER-ID')}`;
    const chatId = route.params.chatId as string;
    const nickname = stringToBase64UrlSafe(userInfo.value.nickname);
    const languageCode = languageSelected.value?.code || 'ko';
    let code = qrCodeInfo.value?.code; // 대화방 코드
    if (!code) {
      code = isPartnerPerson ? qrCodeInfo.value.hostCode : qrCodeInfo.value.guestCode;

      const resultQR = await api.get(`/api/qr/${code}`);
      callStore.setQrCodeInfo(resultQR.data);
    }

    selectedChatId.value = chatId ? chatId : selectedChatId.value;
    selectedUserId.value = userStore.userId;
    selectedNickname.value = nickname;

    const connectionInfo: ConnectionInfo = {
      userId: userInfo.value.userId,
      isRegister: selectedIsVoiceRegistered.value,
      isTssOn: microphoneStore.isTssOn ? '1' : '0',
      voiceId: voiceId.toString(),
      chatId: selectedChatId.value,
      languageCode: languageCode,
      nickname: nickname,
      isPartnerPerson,
      longVoice: ['CONSULTATION_CALL', 'GENERAL_CALL_PARTNER', 'CONFERENCE_CALL_PARTNER'].includes(
        qrCodeInfo.value.qrCodeType
      )
        ? '1'
        : '0',
      code,
      token: authorization,
      evictMySelf: evictMySelf.value,
      callType: qrCodeInfo.value.callType,
      connectionType: 'GENERAL',
      agent,
      ...info,
    };
    socketStore.setConnectInfo(connectionInfo);
    await socketStore.setupWebSocket(isForceful);
  };

  const { alertCallEndedToLogin } = useNotification();

  // 소켓 재연결 시
  watch(
    () => socketStore.recover,
    async (recover) => {
      if (recover) {
        await microphoneStore.clearMic();
        setBottomBarStatus!(-2);
      }
    },
    { immediate: true }
  );

  // 강퇴 당했을 때 처리
  watch(
    () => socketStore.evicted,
    async (newVal) => {
      if (newVal) {
        if (QRCodeType.isConsultationType(qrCodeInfo.value.qrCodeType)) {
          setTimeout(async () => {
            // 1:1통역에서만 5초 이내 재접속시 유지 (네트웍 이슈 등으로)
            if (socketStore.attendees.size <= 1) {
              handleHangUp();
            }
          }, 5000);
        } else {
          handleHangUp();
        }
        if (socketStore.connectInfo?.callType === 'AUDIENCE') {
          alertCallEndedToLogin();
          return;
        }
      }
    },
    { immediate: true }
  );

  return {
    connectToWebsocket,
  };
}
